import type * as React from 'react';

import {
    ALL_CONTENT_PERMISSIONS,
    type DefaultPermissionLevel,
    type OrganizationMemberRoleOrGuest,
} from '@gitbook/app-database';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteGroupDef,
    type PaletteLabelDef,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import type { ButtonProps, IconDefinition } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useUpgrade } from '../../state';

interface PermissionRoleDescriptor {
    title: string;
    description: string;
    icon: SVGIconComponent;
    needAdvancedPermissions?: boolean;
}

/**
 * A permissions dictionnary exposing a title, description, and icon for each permission.
 */
export const PERMISSION_ROLES_DESCRIPTORS: {
    [key in Exclude<DefaultPermissionLevel, null>]: PermissionRoleDescriptor;
} = {
    inherit: {
        title: 'Inherited',
        description: 'Match roles set in the Organization',
        icon: Icon.RoleInherit,
    },
    admin: {
        title: 'Administrator',
        description: 'Can do everything',
        icon: Icon.RoleAdmin,
    },
    create: {
        title: 'Creator',
        description: 'Create, publish, merge and edit',
        icon: Icon.RoleCreator,
    },
    review: {
        title: 'Reviewer',
        description: 'Review and merge change requests',
        icon: Icon.RoleReviewer,
        needAdvancedPermissions: true,
    },
    edit: {
        title: 'Editor',
        description: 'Edit pages, submit change requests',
        icon: Icon.RoleEditor,
    },
    comment: {
        title: 'Commenter',
        description: 'Read content and comment',
        icon: Icon.RoleCommenter,
        needAdvancedPermissions: true,
    },
    read: {
        title: 'Reader',
        description: 'Read-only access',
        icon: Icon.RoleReader,
    },
};

export const PERMISSION_GUEST_ROLE_DESCRIPTOR: PermissionRoleDescriptor = {
    title: 'Guest',
    description: 'No default access',
    icon: Icon.PermissionNone,
};

export const PERMISSION_NO_ACCESS_ROLE_DESCRIPTOR: PermissionRoleDescriptor = {
    title: 'No access',
    description: 'No default access',
    icon: Icon.PermissionNone,
};

type PermissionRoleButtonProps<Role> = {
    /** Mode to use to display the definition of the roles */
    mode?: 'member' | 'content';
    /** Current role, if not specified, no value is shown as selected */
    value?: Role | undefined;
    /** Callback when a new role is seelcted */
    onValueChange: (newValue: Role) => void;
    /** Optional callback to allow deleting the value */
    onRemove?: () => void;
    /** Optional to disable the listing of no access */
    allowNullValue?: boolean;
    /** Optional to allow the inherit value */
    allowInherit?: boolean;
    /** Icon for the button, if passed null it doesn't show the default icon */
    icon?: IconDefinition | null;
    /** Transform the palette */
    extraPaletteGroups?: () => PaletteGroupDef[];
} & Pick<ButtonProps, 'trailingIcon' | 'size' | 'kind' | 'disabled' | 'className' | 'labelStyle'>;

/**
 * Button to select a role.
 */
export function PermissionRoleButton(
    props:
        | (PermissionRoleButtonProps<OrganizationMemberRoleOrGuest> & {
              allowInherit?: false;
          })
        | (PermissionRoleButtonProps<DefaultPermissionLevel> & { allowInherit: true })
): React.ReactElement {
    const {
        mode,
        value,
        onValueChange,
        onRemove,
        icon,
        trailingIcon,
        allowNullValue = true,
        allowInherit = false,
        extraPaletteGroups,
        ...buttonProps
    } = props;

    const upgradeForAdvancedPermissions = useUpgrade('members-advanced-roles');

    const nullDescriptor =
        mode === 'member' ? PERMISSION_GUEST_ROLE_DESCRIPTOR : PERMISSION_NO_ACCESS_ROLE_DESCRIPTOR;
    const currentDescriptor =
        value === undefined
            ? undefined
            : value === null
              ? nullDescriptor
              : PERMISSION_ROLES_DESCRIPTORS[value];

    const palette = () => {
        const roles = [...(allowInherit ? (['inherit'] as const) : []), ...ALL_CONTENT_PERMISSIONS];

        return createPalette({
            position: {
                width: PalettePositionWidth.LARGE,
            },
            groups: [
                ...(extraPaletteGroups ? extraPaletteGroups() : []),
                {
                    key: 'roles',
                    commands: roles
                        .map((role) => {
                            if (role === null && !allowNullValue) {
                                return null;
                            }

                            const descriptor =
                                role === null ? nullDescriptor : PERMISSION_ROLES_DESCRIPTORS[role];
                            const requireUpgrade = descriptor.needAdvancedPermissions
                                ? upgradeForAdvancedPermissions
                                : undefined;
                            return {
                                ...(value !== undefined
                                    ? {
                                          type: PaletteCommandType.CHECKBOX as const,
                                          selected: value === role,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                                key: role || 'guest',
                                icon: descriptor.icon,
                                title: descriptor.title,
                                subtitle: descriptor.description,
                                label: requireUpgrade
                                    ? ({
                                          kind: 'upgrade',
                                          title: requireUpgrade.plan,
                                          icon: Icon.Upgrade,
                                      } as PaletteLabelDef)
                                    : undefined,
                                trigger: () => {
                                    if (requireUpgrade) {
                                        requireUpgrade.onPress();
                                        return;
                                    }

                                    // @ts-ignore the value is accepted when allowInherit is passed
                                    onValueChange(role);
                                },
                            };
                        })
                        .filter(filterOutNullable),
                },
                onRemove
                    ? {
                          key: 'remove',
                          commands: [
                              {
                                  key: 'remove',
                                  title: 'Remove',
                                  icon: Icon.Trashcan,
                                  trigger: () => {
                                      if (onRemove) {
                                          onRemove();
                                      }
                                  },
                              },
                          ],
                      }
                    : null,
            ].filter(filterOutNullable),
        });
    };

    return (
        <PaletteButton
            leadingIcon={icon === null ? undefined : icon || currentDescriptor?.icon}
            trailingIcon={trailingIcon || Icon.ChevronDown}
            label={currentDescriptor?.title || 'Select a role'}
            palette={palette}
            kind={buttonProps.kind ?? 'secondary'}
            {...buttonProps}
        />
    );
}
